import { endOfDay } from 'date-fns';

import { Project } from '../models/project';
import {
  TimesheetUIPreferences,
  WeekDayAbbr,
} from '../models/ui-preferences';
import { TimeDateUtils } from './date-utils';
import { isOfficialLeaveProject } from '../services/official-leave.service';

export interface TimesheetColumnDefinitions {
  displayedColumns: string[];
  displayedPinnedHeaderRow: string[];
  displayedProjWithTypesRow: string[];
  displayedGroupHeaderRow: string[];
  displayedHolidayColumns: string[];
  displayedTotalsColumns: string[];
}

export function weekEnd(day: Date): Date {
  const end = endOfDay(new Date(day));
  const dayOfWeek = end.getDay();
  if (dayOfWeek !== 6) {
    end.setDate(end.getDate() + ((6 - dayOfWeek + 7) % 7));
  }
  return end;
}

export function getWeekDays(
  date: Date,
  daysToHide: WeekDayAbbr[] = [],
  daysWithHours: string[] = [],
): string[] {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const days = <WeekDayAbbr[]>'sun mon tue wed thu fri sat'.split(' ');

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return TimeDateUtils.getDay(d);
  }).filter((d, i) => daysWithHours.includes(d) || !daysToHide.includes(days[i]));
}

export function buildTimesheetColumns(
  weekDays: string[],
): TimesheetColumnDefinitions {
  const empties = [...new Array(weekDays.length)].map((_) => 'empty');

  return {
    displayedColumns: ['project', ...weekDays, 'totals'],
    displayedPinnedHeaderRow: ['pinned', ...empties, 'empty'],
    displayedProjWithTypesRow: [
      'project-with-types-header',
      ...[...empties].map(
        (_, d) => 'empty-proj-type-header-' + weekDays[d].toLowerCase(),
      ),
      'project-total',
    ],
    displayedGroupHeaderRow: [
      'groupName',
      ...[...empties].map(
        (_, d) => 'empty-effort-header-' + weekDays[d].toLowerCase(),
      ),
      'group-total',
    ],
    displayedHolidayColumns: [
      'header-holidays-label',
      ...weekDays.map((wd) => 'holiday-' + wd),
      'header-holidays-total',
    ],
    displayedTotalsColumns: [
      'header-subtotals-label',
      ...weekDays.map((wd) => 'subtotals-' + wd),
      'header-total',
    ],
  };
}

export function hoursForDay(project: Project, day: string): number {
  return parseFloat(project.hours[day] ? project.hours[day] : '0');
}

export function rowTotal(project: Project, weekDays: string[]): number {
  return weekDays.reduce((acc, day) => acc + hoursForDay(project, day), 0);
}

export function pinnedTotal(projects: Project[], weekDays: string[]): number {
  return projects
    .filter((p) => p.ui.groupName === 'Pinned')
    .reduce((acc, cur) => acc + rowTotal(cur, weekDays), 0);
}

export function rowGroupTotal(
  project: Project,
  projects: Project[],
  weekDays: string[],
  usePinnedTotal: boolean,
): number {
  if (usePinnedTotal) return pinnedTotal(projects, weekDays);

  return projects
    .filter((p) => p.effortId == project.effortId && p.ui.groupName !== 'Pinned')
    .reduce((acc, cur) => acc + rowTotal(cur, weekDays), 0);
}

export function rowProjectTotal(
  project: Project,
  projects: Project[],
  weekDays: string[],
): number {
  return projects
    .filter((p) => p.id == project.id && p.ui.groupName !== 'Pinned')
    .reduce((acc, cur) => acc + rowTotal(cur, weekDays), 0);
}

export function timesheetTotal(projects: Project[], weekDays: string[]): number {
  return projects.reduce((acc, cur) => acc + rowTotal(cur, weekDays), 0);
}

export function totalHoursForDay(projects: Project[], day: string): string {
  let total = 0;
  for (let proj of projects) {
    total += Number(hoursForDay(proj, day).toFixed(1));
  }
  return total.toFixed(1);
}

export function totalHoursFloatForDay(projects: Project[], day: string): number {
  return parseFloat(totalHoursForDay(projects, day));
}

export function projectTypeDayTotal(
  project: Project,
  projects: Project[],
  day: string,
): number | '-' {
  const total = projects
    .filter((p) => p.ui.groupName !== 'Pinned' && p.id == project.id)
    .reduce((acc, cur) => acc + hoursForDay(cur, day), 0);

  return total > 0 ? total : '-';
}

export function effortDayTotal(
  project: Project,
  projects: Project[],
  day: string,
): number | '-' {
  const total = projects
    .filter((p) => p.ui.groupName !== 'Pinned' && p.effortId == project.effortId)
    .reduce((acc, cur) => acc + hoursForDay(cur, day), 0);

  return total > 0 ? total : '-';
}

const roundFns = {
  nearest: (n: number) => Math.round(n * 10) / 10,
  down: (n: number) => Math.trunc(n * 10) / 10,
  up: (n: number) => Math.ceil(n * 10) / 10,
};

export function applyHoursFormatting(
  projects: Project[],
  weekDays: string[],
  timesheetPreference?: TimesheetUIPreferences,
): void {
  const roundFn =
    roundFns[timesheetPreference?.hoursRoundingMethod ?? 'down'] ??
    roundFns['down'];

  for (let proj of projects) {
    if (isOfficialLeaveProject(proj)) continue;
    for (let day of weekDays) {
      const hours = proj.hours[day];
      const userRounded = roundFn(
        Number(typeof hours === 'string' ? parseFloat(hours) : hours),
      );
      const formatted = '' + parseFloat(userRounded.toFixed(1));
      if (!isNaN(parseFloat(formatted)) && hours != formatted) {
        proj.hours[day] = formatted;
      }
    }
  }
}
